import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Form, message } from 'antd';
import type { AssetDetailComponent } from '~/app/services/asset/asset.service';
import { AssetComponentService } from '~/app/services/asset-component/asset-component.service';
import { usePermission } from '~/app/auth/hooks/usePermission';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { AssetComponentResponseDto } from '@evimo/types';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = ['10', '20', '50'];

export type ComponentFormValues = {
  name: string;
  serialNumber?: string;
  assetContextId?: string;
  componentType?: string | null;
  connectorId?: number | null;
  ocppId?: string | null;
  controllerId?: string | null;
  installedAt?: dayjs.Dayjs | null;
  removedAt?: dayjs.Dayjs | null;
  attributesJson?: string;
};

function toTableRow(
  component: AssetComponentResponseDto,
): AssetDetailComponent {
  return {
    id: component.id,
    name: component.name,
    serialNumber: component.serialNumber,
    assetContextId: component.assetContextId,
    assetContext: component.assetContext,
    componentType: component.componentType,
    connectorId: component.connectorId,
    ocppId: component.ocppId,
    controllerId: component.controllerId,
    controller: component.controller,
    componentClass: component.componentClass,
    parentComponent: component.parentComponent,
    installedAt: component.installedAt,
    removedAt: component.removedAt,
    attributes: component.attributes,
    createdAt: component.createdAt,
    updatedAt: component.updatedAt,
  };
}

function formatContextLabel(context: {
  assetType: { name: string; code: string };
  subsystem: { name: string; code: string };
  componentClass: { name: string; code: string };
  componentType: { name: string };
}): string {
  return `${context.assetType.name} / ${context.subsystem.name} / ${context.componentClass.name} / ${context.componentType.name}`;
}

function parseAttributesJson(
  value: string | undefined,
  t: (key: string, fallback: string) => string,
): Record<string, unknown> | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (
      parsed === null ||
      typeof parsed !== 'object' ||
      Array.isArray(parsed)
    ) {
      throw new Error(
        t(
          'detail.components.form.attributesMustBeObject',
          'Metadata must be a JSON object',
        ),
      );
    }
    return parsed as Record<string, unknown>;
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error(
        t(
          'detail.components.form.attributesInvalidJson',
          'Metadata must be valid JSON',
        ),
      );
    }
    throw error;
  }
}

export function useAssetDetailComponents(
  organizationId: string,
  assetId: string,
  siblingComponents: AssetDetailComponent[],
  options?: { enabled?: boolean },
) {
  const { t } = useTranslation('assets');
  const { modal } = App.useApp();
  const { hasPermission } = usePermission();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [editingComponent, setEditingComponent] =
    useState<AssetDetailComponent | null>(null);
  const [form] = Form.useForm<ComponentFormValues>();
  const [isSaving, setIsSaving] = useState(false);
  const enabled =
    Boolean(assetId) && Boolean(organizationId) && (options?.enabled ?? true);

  const canUpdate = hasPermission(PermissionKey.COMPONENTS_UPDATE);
  const canDelete = hasPermission(PermissionKey.COMPONENTS_DELETE);

  const listQuery = useQuery({
    queryKey: [
      'assets',
      organizationId,
      assetId,
      'components',
      page,
      pageSize,
      debouncedSearch,
    ],
    queryFn: () =>
      AssetComponentService.list(organizationId, assetId, {
        page,
        limit: pageSize,
        q: debouncedSearch || undefined,
      }),
    enabled,
  });

  const contextsQuery = useQuery({
    queryKey: ['assets', organizationId, assetId, 'component-contexts'],
    queryFn: () => AssetComponentService.listContexts(organizationId, assetId),
    enabled: !!editingComponent && !!organizationId,
  });

  const rows = useMemo(
    () => (listQuery.data?.data ?? []).map(toTableRow),
    [listQuery.data?.data],
  );

  const contextOptions = useMemo(() => {
    const options = (contextsQuery.data?.data ?? []).map((context) => ({
      value: context.id,
      label: formatContextLabel(context),
    }));
    const current = editingComponent?.assetContext;
    if (current && !options.some((option) => option.value === current.id)) {
      options.unshift({
        value: current.id,
        label: formatContextLabel(current),
      });
    }
    return options;
  }, [contextsQuery.data?.data, editingComponent?.assetContext]);

  const ocppRoleOptions = useMemo(
    () => [
      {
        value: 'controller',
        label: t('detail.components.types.controller', 'Controller'),
      },
      {
        value: 'connector',
        label: t('detail.components.types.connector', 'Connector'),
      },
    ],
    [t],
  );

  const controllerOptions = useMemo(() => {
    const byId = new Map<
      string,
      { value: string; label: string; ocppId: string | null }
    >();

    const consider = (component: AssetDetailComponent) => {
      if (component.id === editingComponent?.id) return;
      if (component.componentType !== 'controller') return;
      byId.set(component.id, {
        value: component.id,
        label: component.ocppId
          ? `${component.name} (${component.ocppId})`
          : component.name,
        ocppId: component.ocppId ?? null,
      });
    };

    for (const component of siblingComponents) consider(component);
    for (const component of rows) consider(component);

    const linked = editingComponent?.controller;
    if (linked && !byId.has(linked.id)) {
      byId.set(linked.id, {
        value: linked.id,
        label: linked.ocppId
          ? `${linked.name} (${linked.ocppId})`
          : linked.name,
        ocppId: linked.ocppId ?? null,
      });
    }

    return Array.from(byId.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [siblingComponents, rows, editingComponent]);

  const invalidateLists = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['assets', 'detail', assetId],
      }),
      queryClient.invalidateQueries({
        queryKey: ['assets', assetId, 'components'],
      }),
    ]);
  };

  const applySearch = (value: string) => {
    setSearch(value);
    setDebouncedSearch(value.trim());
    setPage(1);
  };

  const handlePageChange = (nextPage: number, nextPageSize: number) => {
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
      setPage(1);
      return;
    }
    setPage(nextPage);
  };

  const handleEdit = (component: AssetDetailComponent) => {
    setEditingComponent(component);
    form.setFieldsValue({
      name: component.name,
      serialNumber: component.serialNumber ?? undefined,
      assetContextId:
        component.assetContextId ?? component.assetContext?.id ?? undefined,
      componentType: component.componentType ?? null,
      connectorId: component.connectorId ?? null,
      ocppId: component.ocppId ?? undefined,
      controllerId: component.controllerId ?? component.controller?.id ?? null,
      installedAt: component.installedAt ? dayjs(component.installedAt) : null,
      removedAt: component.removedAt ? dayjs(component.removedAt) : null,
      attributesJson: component.attributes
        ? JSON.stringify(component.attributes, null, 2)
        : '',
    });
  };

  const handleCancel = () => {
    setEditingComponent(null);
    form.resetFields();
  };

  const handleDelete = (component: AssetDetailComponent) => {
    modal.confirm({
      title: t('detail.components.deleteConfirmTitle', 'Delete component?'),
      content: t(
        'detail.components.deleteConfirmContent',
        'This permanently deletes "{{name}}". Components in a twins group cannot be deleted.',
        { name: component.name },
      ),
      okType: 'danger',
      onOk: async () => {
        try {
          await AssetComponentService.delete(
            organizationId,
            assetId,
            component.id,
          );
          message.success(
            t(
              'detail.components.deleteSuccess',
              'Component deleted successfully',
            ),
          );
          await invalidateLists();
        } catch (error) {
          message.error(
            error instanceof Error
              ? error.message
              : t(
                  'detail.components.deleteError',
                  'Failed to delete component',
                ),
          );
          throw error;
        }
      },
    });
  };

  const handleSave = async () => {
    if (!editingComponent) return;
    try {
      const values = await form.validateFields();
      const attributes = parseAttributesJson(values.attributesJson, t);
      const controllerId = values.controllerId ?? null;
      const selectedController = controllerOptions.find(
        (option) => option.value === controllerId,
      );
      const ocppIdFromForm = values.ocppId?.trim()
        ? values.ocppId.trim()
        : null;
      const ocppId =
        ocppIdFromForm ??
        (controllerId ? (selectedController?.ocppId ?? null) : null);
      setIsSaving(true);

      await AssetComponentService.update(
        organizationId,
        assetId,
        editingComponent.id,
        {
          name: values.name,
          serialNumber: values.serialNumber ?? null,
          assetContextId: values.assetContextId,
          componentType: values.componentType ?? null,
          connectorId:
            values.connectorId === undefined || values.connectorId === null
              ? null
              : Number(values.connectorId),
          ocppId,
          controllerId,
          installedAt: values.installedAt
            ? values.installedAt.toISOString()
            : null,
          removedAt: values.removedAt ? values.removedAt.toISOString() : null,
          attributes,
        },
      );

      message.success(
        t('detail.components.saveSuccess', 'Component updated successfully'),
      );
      await invalidateLists();
      handleCancel();
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return;
      }
      message.error(
        error instanceof Error
          ? error.message
          : t('detail.components.saveError', 'Failed to update component'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return {
    form,
    rows,
    controllerOptions,
    contextOptions,
    ocppRoleOptions,
    contextsLoading: contextsQuery.isLoading || contextsQuery.isFetching,
    editingComponent,
    isSaving,
    canUpdate,
    canDelete,
    isLoading: listQuery.isLoading || listQuery.isFetching,
    isError: listQuery.isError,
    search,
    page,
    pageSize,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    total: listQuery.data?.meta?.total ?? 0,
    applySearch,
    handlePageChange,
    handleEdit: canUpdate ? handleEdit : undefined,
    handleDelete: canDelete ? handleDelete : undefined,
    handleCancel,
    handleSave,
  };
}
